import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { searchService } from "../../services/api";

// "Player is not ready for" country blacklist (Invariant 5). Value is an array of
// WSArea ids (submitted as blacklist_country_ids); the picker resolves ids ⇆ names
// against /dashboard/countries/. Clubs from a listed country see the calm disabled
// contact state AND the backend rejects the contact attempt.
const CountryBlacklistField = ({ value = [], onChange }) => {
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    let active = true;
    searchService
      .getCountries("")
      .then((res) => {
        if (active && res.success) setCountries(res.data || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const byId = useMemo(() => {
    const m = new Map();
    countries.forEach((c) => m.set(c.id, c));
    return m;
  }, [countries]);

  const selected = value.map((id) => byId.get(id)).filter(Boolean);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return countries
      .filter((c) => !value.includes(c.id))
      .filter((c) => (q ? (c.name || "").toLowerCase().includes(q) : true))
      .slice(0, 40);
  }, [countries, value, query]);

  const add = (id) => {
    onChange([...value, id]);
    setQuery("");
    setOpen(false);
  };
  const remove = (id) => onChange(value.filter((v) => v !== id));

  return (
    <div className="relative" ref={wrapRef}>
      {/* Design 4b: ONE bordered field holding the country chips + the inline
          "Add country…" trigger. Chips carry the platform image flag + a ✕. */}
      <div className="flex flex-wrap items-center gap-[7px] rounded-xl border border-[#DFE6EF] px-[11px] py-[9px]">
        {selected.map((c) => (
          <span
            key={c.id}
            className="inline-flex items-center gap-[7px] rounded-full bg-[#F1F4F8] px-[11px] py-[5px] text-[12px] font-semibold text-[#33475E]"
          >
            {c.image_url && (
              <img
                src={c.image_url}
                alt=""
                className="w-[14px] h-[10px] rounded-[2px] object-cover shadow-[0_0_0_1px_rgba(16,24,40,.08)]"
                referrerPolicy="no-referrer"
              />
            )}
            {c.name}
            <button
              type="button"
              onClick={() => remove(c.id)}
              aria-label={`Remove ${c.name}`}
              className="text-[11px] text-[#98A5B8] hover:text-[#33475E] leading-none"
            >
              ✕
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[12px] text-[#8A98AC] hover:text-[#33475E] px-1 py-[5px]"
        >
          Add country…
        </button>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-[280px] bg-white border border-[#E4E9F1] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.12)] overflow-hidden">
          <div className="relative p-2 border-b border-[#EEF1F6]">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search countries…"
              className="w-full bg-[#F3F4F6] rounded-md pl-8 pr-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto">
            {options.length === 0 ? (
              <p className="px-3 py-3 text-[12.5px] text-[#9CA3AF]">No countries match.</p>
            ) : (
              options.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => add(c.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-[#111827] hover:bg-gray-50"
                >
                  {c.image_url && (
                    <img src={c.image_url} alt="" className="w-4 h-3 object-contain" referrerPolicy="no-referrer" />
                  )}
                  {c.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <p className="mt-1.5 text-[11px] text-[#98A5B8] leading-snug">
        Clubs from these countries see the listing with contact disabled.
      </p>
    </div>
  );
};

export default CountryBlacklistField;
